/**
 * Idle-expiry prompt for the task pane.
 *
 * The inactivity timeout used to end a session with no notice: a pane left
 * open in a background Outlook window was simply signed out, and the next thing
 * the user saw was the sign-in screen — mid-thread, with no idea why. This is
 * the notice, raised a few minutes before the deadline
 * (`INACTIVITY_WARNING_LEAD_MS`), with the two answers that matter: keep working,
 * or sign out now.
 *
 * The provider publishes a fixed deadline rather than a live countdown (see
 * `idleWarningDeadline`), so the once-a-second tick lives here: it re-renders
 * this prompt alone instead of every consumer of the auth context, and it exists
 * only while the prompt is up rather than for every idle minute of the session.
 *
 * That same tick ends the session (`endIdleSession`) when it reaches zero, so
 * the sign-out lands on the second the user is watching for instead of on the
 * next 60s heartbeat. The provider's heartbeat check is still the backstop, so
 * the timeout fires whether or not this prompt is mounted.
 */

import { useEffect, useRef, useState } from "react";
import { useAuthState } from "./AuthStateProvider";
import styles from "./IdleSessionWarning.module.css";

/** Ms left until `deadline`, floored at 0 so the display never goes negative. */
function remainingUntil(deadline: number): number {
  return Math.max(0, deadline - Date.now());
}

/** `m:ss` — the countdown is always under a few minutes, so no hours part. */
function formatCountdown(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function IdleSessionWarning() {
  const { idleWarningDeadline, extendSession, endIdleSession, signOut } = useAuthState();

  // Deliberately stateless: the panel below is keyed on the deadline, so it
  // mounts fresh every time the prompt opens and seeds its countdown from the
  // real deadline on the very first frame. Countdown state held out here would
  // have been created before any deadline existed, and would paint a stale
  // "0:00" for one frame each time the prompt appeared.
  if (idleWarningDeadline == null) return null;

  return (
    <IdleSessionWarningPanel
      key={idleWarningDeadline}
      deadline={idleWarningDeadline}
      onStaySignedIn={extendSession}
      onExpired={endIdleSession}
      onSignOut={signOut}
    />
  );
}

interface IdleSessionWarningPanelProps {
  /** Epoch ms at which the session lapses — the countdown counts down to this. */
  readonly deadline: number;
  readonly onStaySignedIn: () => void;
  readonly onExpired: () => void;
  readonly onSignOut: () => void;
}

function IdleSessionWarningPanel({
  deadline,
  onStaySignedIn,
  onExpired,
  onSignOut,
}: Readonly<IdleSessionWarningPanelProps>) {
  const [remainingMs, setRemainingMs] = useState(() => remainingUntil(deadline));
  const stayButtonRef = useRef<HTMLButtonElement>(null);

  // Redraw the countdown once a second, and end the session when it runs out.
  // Each beat re-reads the clock instead of decrementing a counter, so a starved
  // or throttled interval (routine in an Office webview whose host is
  // backgrounded) reports the true time left on its next beat, and a beat that
  // arrives late lands on zero rather than past it.
  useEffect(() => {
    const id = setInterval(() => {
      const remaining = remainingUntil(deadline);
      setRemainingMs(remaining);
      if (remaining <= 0) {
        // Stop first: `onExpired` takes this prompt down with the session, but
        // clearing here means no beat can fire twice on a lapsed deadline.
        clearInterval(id);
        onExpired();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [deadline, onExpired]);

  // Move focus to the safe action so the prompt is answerable from the keyboard
  // the moment it appears — and so a screen reader announces it.
  useEffect(() => {
    stayButtonRef.current?.focus();
  }, []);

  // Escape keeps the session. Dismissing this prompt cannot mean "carry on
  // counting down invisibly" — that is the silent logout it exists to replace —
  // and of the two answers, keeping a session the user is plainly still at the
  // keyboard for is the one that can't lose their work.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onStaySignedIn();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onStaySignedIn]);

  return (
    <dialog
      open
      className={styles.overlay}
      // alertdialog, not plain dialog: the prompt is time-critical and its
      // description carries the deadline, so it has to be announced on open
      // rather than waiting to be explored.
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-warning-title"
      aria-describedby="idle-warning-body"
      data-testid="idle-session-warning"
    >
      <div className={styles.panel}>
        <h2 id="idle-warning-title" className={styles.title}>
          Still there?
        </h2>
        {/* The countdown carries role="timer" with live updates off: its value is
            announced with the prompt when it opens, without a screen reader then
            reading out every second of the countdown. */}
        <p id="idle-warning-body" className={styles.body}>
          You&rsquo;ve been inactive for a while. AiVA will sign you out in{" "}
          <span className={styles.countdown} role="timer" aria-live="off">
            {formatCountdown(remainingMs)}
          </span>
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            ref={stayButtonRef}
            className={styles.stayBtn}
            onClick={onStaySignedIn}
          >
            Stay signed in
          </button>
          <button type="button" className={styles.signOutBtn} onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </dialog>
  );
}
